// The action framework's `inputSchema` is typed against zod/v3.
import { z } from 'zod/v3';
import React from 'react';
import { ok } from 'neverthrow';
import type {
  ActionTrackMetadata,
  AskResponse,
} from '@cotera/client/v0/actions/framework/types';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { ResourceType } from '@cotera/api';
import { CocoChatModel } from '../../chat/models/coco-chat.model';
import { AgentModel } from '../../agent/agent.model';
import { DocumentModel } from '../../document/document.model';
import { ResourceSelectionModel } from '../selection/resource-selection.model';
import { FolderContentsPageModel } from '../../folder/folder-contents-page.model';
import { ChatArtifactModel } from '@cotera/client/v0/chat/chat-artifact.model';
import {
  artifactToShareableResource,
  collectShareableChatResources,
} from '../../chat/artifacts/shareable-chat-resources';
import { isChatSeedActive } from '../../chat-seed/url';
import { SharePanel, type ShareLayout } from '../share/share-panel';
import { type ShareTarget, shareTargetKey } from '../share/share-targets';

type ModalManager = {
  push: (props: {
    view: () => React.ReactNode;
    onClose?: () => void;
    size?: { custom: string };
  }) => void;
  close: () => void;
};

const shareResourceLinkParamsSchema = z.object({
  /**
   * What to share. Omitted, the action shares whatever is targeted: the
   * selection, or the chat (with its artifacts), agent, document or folder
   * page in view.
   */
  targets: z
    .array(
      z.object({
        type: z.nativeEnum(ResourceType),
        id: z.string().uuid(),
        name: z.string().optional(),
      })
    )
    .min(1)
    .optional(),
  /**
   * From the chat's own Share button: share this chat, or while it is empty the
   * artifact on screen, whatever the page's selection happens to hold. Pages
   * mounted in the panel (a background tab, a mention) re-target the selection
   * at themselves, so it can't be trusted to mean the chat.
   */
  fromChat: z.boolean().optional(),
});

type Params = z.infer<typeof shareResourceLinkParamsSchema>;

function getChatDisplayName(chatModel: CocoChatModel): string | undefined {
  const conversation = chatModel.conversationWatchable.snapshot();
  const serverTitle = conversation?.title()?.trim();
  if (serverTitle !== undefined && serverTitle !== '') {
    return serverTitle;
  }
  const seededTitle = chatModel.seededDisplayTitleWatchable.snapshot()?.trim();
  if (seededTitle !== undefined && seededTitle !== '') {
    return seededTitle;
  }
  return undefined;
}

/**
 * The chat first — it is what the dialog describes — then its artifacts, which
 * the dialog offers to share along with it.
 */
function getChatShareTargets(
  chatModel: CocoChatModel,
  context: ExecuteContext
): { shareTargets: ShareTarget[]; ownTargets: ShareTarget[] } {
  const artifactModel = context.getInScopeModelsOfType(ChatArtifactModel)[0];
  const allArtifacts = artifactModel?.artifactsWatchable.snapshot() ?? [];
  // One question, two consequences: while the seed is still active there is no
  // conversation to share and no conversation-borne artifacts either, so the
  // chat drops out and the seed snapshot is the only artifact list there is.
  // Once it is over, both flip — the chat is real and `artifacts` is
  // authoritative, dismissals included.
  const seedActive = isChatSeedActive({
    searchParams: new URLSearchParams(window.location.search),
    hasDeferredSeedContext: chatModel.hasDeferredSeedContext(),
    messageCount: chatModel.messagesWatchable.snapshot().length,
  });
  // A chat nobody has written in yet (an agent opened from the library lands
  // in one) has nothing to share; what's open in it is the subject instead.
  const empty =
    chatModel.messagesStatusWatchable.snapshot() === 'ready' &&
    !chatModel.hasMessages.snapshot();
  const targets = collectShareableChatResources({
    conversationId: chatModel.conversationId,
    chatDisplayName: getChatDisplayName(chatModel),
    artifacts: allArtifacts,
    deferredSeedArtifacts: chatModel.getDeferredSeedArtifacts(),
    seedActive,
    includeChat: !seedActive && !empty,
  });
  if (!empty && !seedActive) {
    return { shareTargets: targets, ownTargets: targets };
  }
  // With no conversation to hold them together, the artifacts are just tabs:
  // Share means the one on screen (an agent opened from the library).
  const activeId = artifactModel?.activeArtifactIdWatchable.snapshot() ?? null;
  const activeArtifact = allArtifacts.find((a) => a.artifactId === activeId);
  const active =
    activeArtifact === undefined
      ? null
      : artifactToShareableResource(activeArtifact);
  // The listed entry when there is one (it may carry a name); otherwise the
  // artifact itself, since the seed's snapshot can lag behind the panel.
  const shown =
    active === null
      ? undefined
      : targets.find((t) => t.type === active.type && t.id === active.id) ??
        active;
  return {
    shareTargets: shown === undefined ? targets : [shown],
    // Every tab still belongs to this chat: a background tab's page can hold
    // the selection, and that is not a pick from somewhere else.
    ownTargets: [
      ...targets,
      ...allArtifacts.flatMap((artifact) => {
        const resource = artifactToShareableResource(artifact);
        return resource === null ? [] : [resource];
      }),
    ],
  };
}

/** Everything in view that could be what "Share" means. */
export type ShareContextInputs = {
  explicitTargets: ShareTarget[] | undefined;
  /** Asked for from the chat's own Share button (see `fromChat`). */
  fromChat?: boolean;
  selections: ShareTarget[];
  /** The chat on screen, with what sharing it would include. */
  openChat: {
    conversationId: string;
    shareTargets: ShareTarget[];
    /**
     * Everything that belongs to the chat, to recognise the page's own
     * selection; more than `shareTargets` when an empty chat shares only
     * the artifact on screen. Defaults to `shareTargets`.
     */
    ownTargets?: ShareTarget[];
  } | null;
  agentId: string | null;
  documentId: string | null;
  folderId: string | null;
};

/** What the panel shares, and how it presents it. */
export function resolveShare(inputs: ShareContextInputs): {
  targets: ShareTarget[];
  layout: ShareLayout;
} {
  const { explicitTargets, selections, openChat } = inputs;
  if (explicitTargets !== undefined && explicitTargets.length > 0) {
    return {
      targets: explicitTargets,
      layout: explicitTargets.length > 1 ? 'bulk' : 'single',
    };
  }

  // On a chat page the selection is not a pick from a list. The chat keeps its
  // own chat selected (so Move and Archive work), and every artifact page it
  // mounts — agent, document, dataset — overwrites that with its own resource.
  // So a selection made only of the open chat's own things means "this chat",
  // shared the chat way, with its artifacts. A selection of anything else (a
  // sidebar row's menu while a chat is open, say) is shared as picked.
  const openChatKeys = new Set(
    (openChat?.ownTargets ?? openChat?.shareTargets ?? []).map((t) =>
      shareTargetKey(t)
    )
  );
  // The page's own selection of its chat counts even when the chat itself
  // isn't offered (an empty one).
  if (openChat !== null) {
    openChatKeys.add(`${ResourceType.Chat}:${openChat.conversationId}`);
  }
  const selectionIsOpenChat =
    openChat !== null &&
    selections.length > 0 &&
    (inputs.fromChat === true ||
      selections.every((t) => openChatKeys.has(shareTargetKey(t))));

  if (selections.length > 0 && !selectionIsOpenChat) {
    return {
      targets: selections,
      layout: selections.length > 1 ? 'bulk' : 'single',
    };
  }

  if (openChat !== null && openChat.shareTargets.length > 0) {
    const targets = openChat.shareTargets;
    const [first] = targets;
    // Before the chat exists, or while it is empty, only what's open in it can
    // be shared, and none of that is the subject the rest hang off: one open
    // agent is shared as that agent.
    const layout =
      first?.type === ResourceType.Chat
        ? 'bundle'
        : targets.length > 1
        ? 'bulk'
        : 'single';
    return { targets, layout };
  }
  // An empty chat with nothing open in it: the page's own resource, if any.

  if (inputs.agentId !== null) {
    return {
      targets: [{ type: ResourceType.Agent, id: inputs.agentId }],
      layout: 'single',
    };
  }
  if (inputs.documentId !== null) {
    return {
      targets: [{ type: ResourceType.Document, id: inputs.documentId }],
      layout: 'single',
    };
  }
  if (inputs.folderId !== null) {
    return {
      targets: [{ type: ResourceType.Folder, id: inputs.folderId }],
      layout: 'single',
    };
  }
  // Last, the selection as it stands (an empty chat's own row).
  return {
    targets: selections,
    layout: selections.length > 1 ? 'bulk' : 'single',
  };
}

function readShareContext(
  params: Partial<Params>,
  context: ExecuteContext
): ShareContextInputs {
  const chatModel = context.targetedModelOfType(CocoChatModel);
  const selection = context.targetedModelOfType(ResourceSelectionModel);
  return {
    explicitTargets: params.targets,
    fromChat: params.fromChat === true,
    selections: (selection?.selections ?? []).map((s) => ({
      type: s.resourceType,
      id: s.resourceId,
      name: s.name,
    })),
    openChat:
      chatModel === null
        ? null
        : {
            conversationId: chatModel.conversationId,
            ...getChatShareTargets(chatModel, context),
          },
    agentId: context.targetedModelOfType(AgentModel)?.agentId ?? null,
    documentId: context.targetedModelOfType(DocumentModel)?.documentId ?? null,
    folderId:
      context.targetedModelOfType(FolderContentsPageModel)?.folderId ?? null,
  };
}

export class ShareResourceLinkAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Share';
  /**
   * Acts on whatever resource is targeted, so it belongs to that resource's own
   * menu, not to a workspace-wide search. It stays registered and invokable —
   * `discrete` only keeps it out of the palette's list.
   */
  discrete = true;

  description = 'Choose who can access this, or get a link';
  inputSchema = shareResourceLinkParamsSchema;
  group = 'app';
  icon = 'share';

  constructor(private readonly modalManager: ModalManager) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      context.hasTargetedModelOfType(CocoChatModel) ||
      context.hasTargetedModelOfType(AgentModel) ||
      context.hasTargetedModelOfType(DocumentModel) ||
      context.hasTargetedModelOfType(FolderContentsPageModel) ||
      context.hasTargetedModelOfType(ResourceSelectionModel)
    );
  }

  getPriority(context: ApplicableContext): number {
    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null && selection.selections.length > 0) {
      return 20;
    }
    return 0;
  }

  shouldAsk(): boolean {
    return true;
  }

  /**
   * Nothing left to do: every change is made inside the dialog as it happens
   * and tracked there, so closing it just resolves the action.
   */
  async execute(): Promise<ActionResult<{ t: string; message?: string }[]>> {
    return ok({});
  }

  ask(
    params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const { targets, layout } = resolveShare(readShareContext(params, context));

    return {
      view: ({ onCancel }) => {
        const resolveClosed = () => {
          void onCancel();
        };
        this.modalManager.push({
          view: () => (
            <SharePanel
              targets={targets}
              layout={layout}
              onClose={() => {
                this.modalManager.close();
                resolveClosed();
              }}
            />
          ),
          onClose: resolveClosed,
          // Sized by the panel itself. The default modal is at least a third
          // of the screen wide, which left empty space beside the panel.
          size: { custom: 'max-w-[94vw]' },
        });
      },
    };
  }

  /** Tracked per change inside the dialog instead (see `useApplyShareChange`). */
  track(): ActionTrackMetadata | null {
    return null;
  }
}
